/**
 * React Query Hooks for System Configuration
 * MCP, Mem0, First Message, and Audit operations
 */

import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { systemApi } from '@/lib/api/system';
import type {
  MCPConfig,
  MCPServer,
  Mem0Config,
  Mem0Defaults,
  FirstMessageConfig,
  AuditLogResponse,
  ArtifactTypesConfig,
  ArtifactType,
  ArtifactTypeConfig,
  ToolEnhancementConfig,
  ToolComboConfig,
  ToolComboTemplateSummary,
  ModelDefaultsConfig,
} from '@/types/system';

// Query Keys
export const SYSTEM_QUERY_KEYS = {
  system: ['system'] as const,
  mcp: ['system', 'mcp'] as const,
  mcpServers: ['system', 'mcp', 'servers'] as const,
  mem0: ['system', 'mem0'] as const,
  mem0Defaults: ['system', 'mem0', 'defaults'] as const,
  firstMessage: ['system', 'first-message'] as const,
  artifactTypes: ['system', 'artifact-types'] as const,
  artifactTypesList: ['system', 'artifact-types', 'list'] as const,
  toolEnhancement: ['system', 'tool-enhancement'] as const,
  toolCombo: ['system', 'tool-combo'] as const,
  toolComboTemplates: ['system', 'tool-combo', 'templates'] as const,
  modelDefaults: ['system', 'model-defaults'] as const,
  audit: (type: string) => ['system', 'audit', type] as const,
};

// MCP Hooks
export function useMCPConfig(): UseQueryResult<MCPConfig, Error> {
  return useQuery({
    queryKey: SYSTEM_QUERY_KEYS.mcp,
    queryFn: () => systemApi.mcp.get(),
  });
}

export function useMCPServers(): UseQueryResult<MCPServer[], Error> {
  return useQuery({
    queryKey: SYSTEM_QUERY_KEYS.mcpServers,
    queryFn: () => systemApi.mcp.listServers(),
    refetchInterval: 10000, // Refresh every 10s to update connection status
  });
}

export function useUpdateMCPConfig(): UseMutationResult<MCPConfig, Error, MCPConfig> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: MCPConfig) => systemApi.mcp.update(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.mcp });
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.mcpServers });
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.audit('mcp') });
    },
  });
}

export function useTestMCPConnection(): UseMutationResult<
  { success: boolean; error?: string },
  Error,
  string
> {
  return useMutation({
    mutationFn: (serverId: string) => systemApi.mcp.testConnection(serverId),
  });
}

export function useReloadMCPManager(): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => systemApi.mcp.reload(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.mcpServers });
    },
  });
}

// Mem0 Hooks
export function useMem0Config(): UseQueryResult<Mem0Config, Error> {
  return useQuery({
    queryKey: SYSTEM_QUERY_KEYS.mem0,
    queryFn: () => systemApi.mem0.get(),
  });
}

export function useMem0Defaults(): UseQueryResult<Mem0Defaults, Error> {
  return useQuery({
    queryKey: SYSTEM_QUERY_KEYS.mem0Defaults,
    queryFn: () => systemApi.mem0.getDefaults(),
  });
}

export function useUpdateMem0Config(): UseMutationResult<Mem0Config, Error, Mem0Config> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: Mem0Config) => systemApi.mem0.update(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.mem0 });
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.audit('mem0') });
    },
  });
}

// First Message Hooks
export function useFirstMessageConfig(): UseQueryResult<FirstMessageConfig, Error> {
  return useQuery({
    queryKey: SYSTEM_QUERY_KEYS.firstMessage,
    queryFn: () => systemApi.firstMessage.get(),
  });
}

export function useUpdateFirstMessageConfig(): UseMutationResult<
  FirstMessageConfig,
  Error,
  FirstMessageConfig
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: FirstMessageConfig) => systemApi.firstMessage.update(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.firstMessage });
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.audit('first-message') });
    },
  });
}

// Audit Hooks
export function useAuditTypes(): UseQueryResult<{ types: string[]; total: number }, Error> {
  return useQuery({
    queryKey: ['system', 'audit', 'types'] as const,
    queryFn: () => systemApi.audit.getTypes(),
  });
}

export function useAuditLogs(type: string): UseQueryResult<AuditLogResponse, Error> {
  return useQuery({
    queryKey: SYSTEM_QUERY_KEYS.audit(type),
    queryFn: () => systemApi.audit.get(type),
    enabled: !!type,
  });
}

// Artifact Types Hooks
export function useArtifactTypesConfig(): UseQueryResult<ArtifactTypesConfig, Error> {
  return useQuery({
    queryKey: SYSTEM_QUERY_KEYS.artifactTypes,
    queryFn: () => systemApi.artifactTypes.get(),
  });
}

export function useArtifactTypes(): UseQueryResult<ArtifactType[], Error> {
  return useQuery({
    queryKey: SYSTEM_QUERY_KEYS.artifactTypesList,
    queryFn: () => systemApi.artifactTypes.list(),
  });
}

export function useUpdateArtifactTypesConfig(): UseMutationResult<ArtifactTypesConfig, Error, ArtifactTypesConfig> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: ArtifactTypesConfig) => systemApi.artifactTypes.update(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.artifactTypes });
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.artifactTypesList });
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.audit('artifact-types') });
    },
  });
}

export function useSaveArtifactType(): UseMutationResult<
  ArtifactTypeConfig,
  Error,
  { typeId: string; config: ArtifactTypeConfig; isNew?: boolean }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ typeId, config, isNew }) =>
      isNew
        ? systemApi.artifactTypes.addType(typeId, config)
        : systemApi.artifactTypes.updateType(typeId, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.artifactTypes });
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.artifactTypesList });
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.audit('artifact-types') });
    },
  });
}

export function useDeleteArtifactType(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (typeId: string) => systemApi.artifactTypes.deleteType(typeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.artifactTypes });
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.artifactTypesList });
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.audit('artifact-types') });
    },
  });
}

// Tool Enhancement Hooks
export function useToolEnhancementConfig(): UseQueryResult<ToolEnhancementConfig, Error> {
  return useQuery({
    queryKey: SYSTEM_QUERY_KEYS.toolEnhancement,
    queryFn: () => systemApi.toolEnhancement.get(),
  });
}

export function useUpdateToolEnhancementConfig(): UseMutationResult<ToolEnhancementConfig, Error, ToolEnhancementConfig> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: ToolEnhancementConfig) => systemApi.toolEnhancement.update(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.toolEnhancement });
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.audit('tool-enhancement') });
    },
  });
}

// Tool Combo Hooks
export function useToolComboConfig(): UseQueryResult<ToolComboConfig, Error> {
  return useQuery({
    queryKey: SYSTEM_QUERY_KEYS.toolCombo,
    queryFn: () => systemApi.toolCombo.get(),
  });
}

export function useToolComboTemplates(enabledOnly = false, language = 'cn'): UseQueryResult<ToolComboTemplateSummary[], Error> {
  return useQuery({
    queryKey: [...SYSTEM_QUERY_KEYS.toolComboTemplates, enabledOnly, language],
    queryFn: () => systemApi.toolCombo.list(enabledOnly, language),
  });
}

export function useUpdateToolComboConfig(): UseMutationResult<ToolComboConfig, Error, ToolComboConfig> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: ToolComboConfig) => systemApi.toolCombo.update(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.toolCombo });
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.toolComboTemplates });
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.audit('tool-combo') });
    },
  });
}

// Model Defaults Hooks
export function useModelDefaultsConfig(): UseQueryResult<ModelDefaultsConfig, Error> {
  return useQuery({
    queryKey: SYSTEM_QUERY_KEYS.modelDefaults,
    queryFn: () => systemApi.modelDefaults.get(),
  });
}

export function useUpdateModelDefaultsConfig(): UseMutationResult<
  { message: string; version: number },
  Error,
  ModelDefaultsConfig
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: ModelDefaultsConfig) => systemApi.modelDefaults.update(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.modelDefaults });
      queryClient.invalidateQueries({ queryKey: SYSTEM_QUERY_KEYS.audit('model-defaults') });
    },
  });
}
